import { Select, SelectItem } from '@carbon/react';
import { asProblem, ListTable, PageHeader, problemMessage, StatusTag, useFormat } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ACCOUNT_STATUSES,
  accountsKey,
  listAccounts,
  STATUS_TAG,
  type AccountStatus,
  type CustomerAccountItem,
} from './customerAccountsApi';

/**
 * Website and app customer accounts (ADR 0082), for support: found by an exact phone or email, or the beginning of a name; contact
 * masked by the server; newest sign-in first. A row opens the account.
 */
export function CustomerAccountsPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormat();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const accounts = useQuery({
    queryKey: accountsKey(search, status, paging.page, paging.pageSize),
    queryFn: () => listAccounts(search, status, paging.page, paging.pageSize),
    placeholderData: keepPreviousData,
  });

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('customerAccounts.title')} description={t('customerAccounts.intro')} />
      <ListTable<CustomerAccountItem>
        ariaLabel={t('customerAccounts.title')}
        loading={accounts.isLoading}
        error={accounts.error ? problemMessage(t, asProblem(accounts.error)) : undefined}
        onRetry={() => void accounts.refetch()}
        rows={accounts.data?.items ?? []}
        search={{
          label: t('customerAccounts.search'),
          onChange: (value) => {
            setSearch(value.trim());
            setPaging((p) => ({ ...p, page: 1 }));
          },
        }}
        toolbarActions={
          <Select
            id="customer-accounts-status"
            labelText={t('customerAccounts.columns.status')}
            hideLabel
            inline
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPaging((p) => ({ ...p, page: 1 }));
            }}
          >
            <SelectItem value="" text={t('customerAccounts.allStatuses')} />
            {ACCOUNT_STATUSES.map((s) => (
              <SelectItem key={s} value={s} text={t(`customerAccounts.status.${s}`)} />
            ))}
          </Select>
        }
        onRowClick={(a) => void navigate(`/online/customers/${a.id}`)}
        rowLabel={(a) => a.name ?? a.maskedPhone ?? a.id}
        empty={{ heading: t(search ? 'customerAccounts.noMatch' : 'customerAccounts.empty') }}
        pagination={{ ...paging, total: Number(accounts.data?.total ?? 0), capped: accounts.data?.totalCapped, onChange: setPaging }}
        columns={[
          { key: 'name', header: t('customerAccounts.columns.name'), render: (a) => a.name ?? '—' },
          { key: 'maskedPhone', header: t('customerAccounts.columns.phone'), render: (a) => a.maskedPhone ?? '—' },
          { key: 'maskedEmail', header: t('customerAccounts.columns.email'), render: (a) => a.maskedEmail ?? '—' },
          {
            key: 'memberAccountNo',
            header: t('customerAccounts.columns.member'),
            render: (a) => a.memberAccountNo ?? '—',
          },
          {
            key: 'lastLoginAt',
            header: t('customerAccounts.columns.lastLogin'),
            render: (a) => (a.lastLoginAt ? dateTime(a.lastLoginAt) : '—'),
          },
          {
            key: 'status',
            header: t('customerAccounts.columns.status'),
            render: (a) => (
              <StatusTag status={STATUS_TAG[a.status as AccountStatus] ?? 'inactive'}>
                {t(`customerAccounts.status.${a.status as AccountStatus}`)}
              </StatusTag>
            ),
          },
        ]}
      />
    </section>
  );
}
